<template>
  <section class="costs-breakdown default" :class="{ desktop: $isDesktop }">
    <section class="cost-section your-booking">
      <table>
        <tbody>
          <tr>
            <td colspan="2" class="your-booking-row">
              <translate class="amount"> Your booking </translate>
              <i18n-t keypath="From {price}">
                <template #price>
                  <pu-price
                    v-if="pitchtype.leadPrice"
                    :price="pitchtype.leadPrice"
                    :display-original-price="true"
                  />
                </template>
              </i18n-t>
            </td>
          </tr>
          <tr>
            <td colspan="2">
              <div class="dates-summary row">
                <translate
                  v-if="pitchtype.personsIncluded > 2"
                  :tt="{
                    nights: pitchtype.leadPriceNights,
                    personsIncluded: pitchtype.personsIncluded,
                  }"
                >
                  Based on {nights}, {personsIncluded} people
                </translate>
                <translate v-else :tt="{ nights: pitchtype.leadPriceNights }">
                  Based on {nights}, 2 adults
                </translate>
              </div>
            </td>
          </tr>
          <tr v-if="pitchtype.excludedPrice">
            <td colspan="2">
              <div class="dates-summary row">
                <div>
                  * <translate>plus taxes or fees</translate>
                  <pu-price
                    :price="pitchtype.excludedPrice"
                    :display-original-price="true"
                    class="pitch-price"
                  />
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td
              colspan="2"
              class="max-capacity-msg"
              :class="{
                'max-capacity-msg-dogs': hasDogs,
              }"
            >
              <span
                v-if="hasDogs"
                :class="{ 'search-error': partyValidation.isOverCapacity }"
              >
                <translate :tt="{ adults: party.adults }">
                  {adults} adults </translate
                ><span>, </span
                ><translate v-if="hasChildren" :tt="{ childrenCount }">
                  {childrenCount} children </translate
                ><span v-if="hasChildren">, </span
                ><translate :tt="{ dogs }"> {dogs} dogs </translate>
              </span>
              <translate
                v-else-if="hasChildren"
                :tt="{ adults: party.adults, childrenCount }"
                :class="{ 'search-error': partyValidation.isOverCapacity }"
              >
                {adults} adults, {childrenCount} children
              </translate>
              <translate
                v-else
                :class="{ 'search-error': partyValidation.isOverCapacity }"
                :tt="{ adults: party.adults }"
              >
                {adults} adults
              </translate>
              <party-edit
                :pitchtype="pitchtype"
                :party="party"
                :party-validation="partyValidation"
                :pitchtype-capacity="pitchtype.capacity"
                :persons-included="pitchtype.personsIncluded"
                :capacity-display="pitchtype.capacityDisplay"
                :show-party-details="false"
              />
              <capacity-rules
                v-if="partyValidation.isOverCapacity"
                :age-restrictions="ageRestrictions"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <i18n-t
      tag="p"
      keypath="{chooseYourDates} and party size to see pricing and availability."
      class="costs-breakdown-note"
    >
      <template #chooseYourDates>
        <translate
          class="choose-date-link"
          @click="$emit('check-availability')"
        >
          Choose your dates
        </translate>
      </template>
    </i18n-t>

    <cancellation-policy-popover
      :campsite-name="campsite.name"
      :campsite-cancellation-policy="campsite.cancellationPolicy"
    />
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type {
  AgeRestrictions,
  CostsBreakdownCampsiteDetails,
  Party,
  PitchType,
} from '../../../campsite-details/types'
import type { PartyValidationResult } from '../../../../utility/campsite/party-validation'

@Component
class DefaultCostsBreakdown extends Vue {
  @Prop({ required: true })
  pitchtype: PitchType

  @Prop()
  campsite: CostsBreakdownCampsiteDetails

  @Prop({ required: true })
  party: Party

  @Prop({ required: true })
  partyValidation: PartyValidationResult

  @Prop()
  ageRestrictions: AgeRestrictions

  get childrenCount() {
    return this.party ? this.party.childAges.length : 0
  }

  get dogs() {
    return this.party?.dogs || 0
  }

  get hasChildren(): boolean {
    return this.childrenCount > 0
  }

  get hasDogs(): boolean {
    return this.dogs > 0
  }
}

export default toNative(DefaultCostsBreakdown)
</script>

<style>
@import url('~/assets/css/costs-breakdown.css');
</style>
